import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, FileText, Calendar, Clock,
  DollarSign, Activity, Target, Award, AlertCircle, CheckCircle, PieChart,
  ArrowUpRight, ArrowDownRight, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');
  const [stats, setStats] = useState({
    totalPatients: 0,
    activePatients: 0,
    newPatients: 0,
    totalVisits: 0,
    completedVisits: 0,
    missedVisits: 0,
    pendingDocumentation: 0,
    completedDocumentation: 0,
    avgVisitsPerDay: 0,
    patientSatisfaction: 0,
    revenueThisMonth: 0,
    revenueLastMonth: 0
  });
  const [visitTrend, setVisitTrend] = useState([]);
  const [documentationStats, setDocumentationStats] = useState([]);
  const [staffPerformance, setStaffPerformance] = useState([]);

  const getToken = () => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/analytics/dashboard?range=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
        setVisitTrend(data.visitTrend || []);
        setDocumentationStats(data.documentationStats || []);
        setStaffPerformance(data.staffPerformance || []);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Use mock data for demo
      setStats({
        totalPatients: 156,
        activePatients: 89,
        newPatients: 12,
        totalVisits: 423,
        completedVisits: 398,
        missedVisits: 25,
        pendingDocumentation: 34,
        completedDocumentation: 389,
        avgVisitsPerDay: 14.1,
        patientSatisfaction: 94.5,
        revenueThisMonth: 127500,
        revenueLastMonth: 118200
      });
      setVisitTrend([
        { week: 'Week 1', visits: 95, completed: 90 },
        { week: 'Week 2', visits: 108, completed: 102 },
        { week: 'Week 3', visits: 112, completed: 105 },
        { week: 'Week 4', visits: 108, completed: 101 }
      ]);
      setDocumentationStats([
        { type: 'SOC/ROC', completed: 45, pending: 5, percentage: 90 },
        { type: 'Visit Notes', completed: 234, pending: 12, percentage: 95 },
        { type: 'Recertification', completed: 28, pending: 8, percentage: 78 },
        { type: '485 POC', completed: 56, pending: 6, percentage: 90 },
        { type: 'Discharge', completed: 26, pending: 3, percentage: 90 }
      ]);
      setStaffPerformance([
        { name: 'Sarah Johnson, RN', visits: 78, documentation: 98, satisfaction: 96 },
        { name: 'Michael Chen, PT', visits: 65, documentation: 95, satisfaction: 94 },
        { name: 'Emily Rodriguez, OT', visits: 52, documentation: 92, satisfaction: 97 },
        { name: 'James Wilson, RN', visits: 71, documentation: 89, satisfaction: 93 },
        { name: 'Lisa Martinez, SLP', visits: 45, documentation: 96, satisfaction: 95 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentChange = (current, previous) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, prefix = '', suffix = '', color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      amber: 'bg-amber-50 text-amber-600',
      purple: 'bg-purple-50 text-purple-600',
      red: 'bg-red-50 text-red-600'
    };
    
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">
              {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
            </p>
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{Math.abs(trend)}% {trendValue}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="analytics-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-[#f39c12]" />
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Performance metrics and productivity insights</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border rounded-lg text-sm font-medium"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
            <Button variant="outline" onClick={fetchAnalytics} className="gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Active Patients"
            value={stats.activePatients}
            icon={Users}
            trend={calculatePercentChange(stats.activePatients, stats.activePatients - stats.newPatients)}
            trendValue="vs last period"
            color="blue"
          />
          <StatCard
            title="Completed Visits"
            value={stats.completedVisits}
            icon={CheckCircle}
            trend={((stats.completedVisits / stats.totalVisits) * 100 - 90).toFixed(1)}
            trendValue="completion rate"
            color="green"
          />
          <StatCard
            title="Pending Documentation"
            value={stats.pendingDocumentation}
            icon={FileText}
            color="amber"
          />
          <StatCard
            title="Monthly Revenue"
            value={stats.revenueThisMonth}
            icon={DollarSign}
            prefix="$"
            trend={calculatePercentChange(stats.revenueThisMonth, stats.revenueLastMonth)}
            trendValue="vs last month"
            color="purple"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Visit Trend */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Visit Trend
            </h3>
            <div className="space-y-4">
              {visitTrend.map((week, index) => (
                <div key={index} className="flex items-center gap-4">
                  <span className="w-20 text-sm text-gray-500">{week.week}</span>
                  <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                    <div 
                      className="absolute left-0 top-0 h-full bg-blue-500 rounded-lg"
                      style={{ width: `${(week.visits / 120) * 100}%` }}
                    />
                    <div 
                      className="absolute left-0 top-0 h-full bg-green-500 rounded-lg opacity-70"
                      style={{ width: `${(week.completed / 120) * 100}%` }}
                    />
                  </div>
                  <span className="w-16 text-sm text-gray-600 text-right">{week.completed}/{week.visits}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded" />
                <span className="text-sm text-gray-500">Total Visits</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded" />
                <span className="text-sm text-gray-500">Completed</span>
              </div>
            </div>
          </div>

          {/* Documentation Status */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-500" />
              Documentation Status
            </h3>
            <div className="space-y-4">
              {documentationStats.map((doc, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{doc.type}</span>
                    <span className="text-sm text-gray-500">{doc.completed}/{doc.completed + doc.pending}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        doc.percentage >= 90 ? 'bg-green-500' : 
                        doc.percentage >= 75 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${doc.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Staff Performance */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Staff Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Staff Member</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Visits</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Documentation %</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Patient Satisfaction</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Performance</th>
                </tr>
              </thead>
              <tbody>
                {staffPerformance.map((staff, index) => {
                  const avgScore = (staff.documentation + staff.satisfaction) / 2;
                  return (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-800">{staff.name}</span>
                      </td>
                      <td className="text-center py-3 px-4 text-gray-600">{staff.visits}</td>
                      <td className="text-center py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          staff.documentation >= 95 ? 'bg-green-100 text-green-700' :
                          staff.documentation >= 90 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {staff.documentation}%
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          staff.satisfaction >= 95 ? 'bg-green-100 text-green-700' :
                          staff.satisfaction >= 90 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {staff.satisfaction}%
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                avgScore >= 95 ? 'bg-green-500' : avgScore >= 90 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${avgScore}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{avgScore.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Avg Visits/Day</span>
            </div>
            <p className="text-2xl font-bold">{stats.avgVisitsPerDay}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Visit Completion</span>
            </div>
            <p className="text-2xl font-bold">{((stats.completedVisits / stats.totalVisits) * 100).toFixed(1)}%</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Patient Satisfaction</span>
            </div>
            <p className="text-2xl font-bold">{stats.patientSatisfaction}%</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">New Patients</span>
            </div>
            <p className="text-2xl font-bold">{stats.newPatients}</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsDashboardPage;
